import {ApiManager} from './api/api-manager';
import {Status} from './status';
import {EventTracker} from './data-collection/event-tracker';
import {ErrorModel} from "./api/error/error-model";
import {ErrorCode} from "./api/error/error-code";
import {Config} from "../config";
import {SimpleEventEmitter} from "./simple-event-emitter";
import {HoneypotFields, HoneypotFieldSnapshot} from "./honeypot/honeypot-fields";

export class CaptchaBox {

  readonly statusChangedEvent = new SimpleEventEmitter();
  readonly captchaFinishEvent = new SimpleEventEmitter();

  public status: Status = Status.START
  public config: Config;
  readonly htmlElement: HTMLElement;
  readonly boxCreationTimestamp = new Date();
  readonly boxCreationPerformanceMark = performance.now();
  readonly eventTracker: EventTracker;
  readonly honeypotFields = new HoneypotFields();
  public verificationToken: string | null = null;
  private formElement: HTMLFormElement;
  private verificationTokenField: HTMLInputElement;
  private apiManager: ApiManager | null = null;
  private timerId?: number;
  private formListeners: Array<{ target: EventTarget; type: string; handler: EventListener }> = [];

  public constructor(config: Config, htmlElement: HTMLElement) {
    this.config = config;
    this.htmlElement = htmlElement;
    this.eventTracker = new EventTracker(!config.minimalDataMode);
  }

  public setup() {
    if (this.status === Status.FAILED) return;

    this.formElement = this.htmlElement.closest('form');

    if (this.formElement != null) {
      this.honeypotFields.setup(this.formElement, this.boxCreationPerformanceMark);
      this.verificationTokenField = this.createVerificationTokenField();
      this.registerFormResetListener();
      this.registerElementFocusListeners();
    } else {
      this.setErrorStatus(new ErrorModel(ErrorCode.NO_FORM_FOUND, "No parent form element found."));
    }
  }

  public getHoneypotSnapshot(): HoneypotFieldSnapshot[] {
    return this.honeypotFields.getSnapshot();
  }

  private createVerificationTokenField(): HTMLInputElement {

    const verificationTokenField = document.createElement('input');
    verificationTokenField.setAttribute('type', 'hidden');
    verificationTokenField.setAttribute('name', this.config.tokenFieldName);

    return verificationTokenField;
  }

  private registerFormResetListener(): void {
    const resetHandler: EventListener = () => {
      this.verificationTokenField?.removeAttribute('value');
    };
    this.addFormListener(this.formElement, 'reset', resetHandler);
  }

  private registerElementFocusListeners() {

    const keydownHandler: EventListener = (event) => {
      if (event instanceof KeyboardEvent) {
        if (event.key === 'Tab') {
          return;
        }
        if (event.target === this.htmlElement) {
          return;
        }
        const target = event.target as Element | null;
        if (target && typeof target.closest === 'function' && target.closest('[data-autostart="false"]')) {
          return;
        }
        this.autostartVerification();
      }
    };
    this.addFormListener(this.formElement, 'keydown', keydownHandler);

    this.formElement.querySelectorAll('input, select, textarea').forEach(element => {
      const focusHandler: EventListener = (event) => {
        const target = event.target as Element | null;
        if (target && typeof target.closest === 'function' && target.closest('[data-autostart="false"]')) {
          return;
        }
        this.autostartVerification();
      };
      this.addFormListener(element, 'focusin', focusHandler);
    });
  }

  private addFormListener(target: EventTarget, type: string, handler: EventListener): void {
    target.addEventListener(type, handler);
    this.formListeners.push({ target, type, handler });
  }

  private unregisterElementFocusListeners(): void {
    for (const { target, type, handler } of this.formListeners) {
      try {
        target.removeEventListener(type, handler);
      } catch {
        // ignore
      }
    }
    this.formListeners = [];
  }

  public autostartVerification() {
    if (!this.config.autostartDisabled) {
      this.startVerification();
    }
  }

  public startVerification() {
    if (this.status !== Status.START) {
      return;
    }

    this.switchStatus(Status.RUNNING);

    this.apiManager = new ApiManager(
      this,
      (verificationToken, expiresInMs) => {
        this.eventTracker.stop();
        this.finishVerification(verificationToken, expiresInMs);
        this.apiManager?.reset();
        this.apiManager = null;
      },
      (reason) => {
        this.eventTracker.stop();
        this.setErrorStatus(reason);
        this.apiManager?.reset();
        this.apiManager = null;
      }
    );

    this.apiManager.verify();
  }

  private finishVerification(verificationToken: string, expiresInMs: number): void {

    this.verificationToken = verificationToken;

    this.verificationTokenField.value = verificationToken;
    this.formElement.appendChild(this.verificationTokenField);

    if (this.timerId) clearTimeout(this.timerId);
    this.timerId = window.setTimeout(() => this.reset(true), Math.max(0, expiresInMs - 30_000));

    this.handleCaptchaSolved();
  }

  private handleCaptchaSolved(): void {

    if (this.verificationToken) {
      this.captchaFinishEvent.emit("captchaSolved", this.verificationToken);
      this.switchStatus(Status.DONE);
    }
  }

  private switchStatus(nextStatus: Status): void {

    this.status = nextStatus;
    this.statusChangedEvent.emit('statusChanged', nextStatus);
  }

  public setErrorStatus(errorModel: ErrorModel): void {
    if (this.status === Status.FAILED) return;
    this.switchStatus(Status.FAILED);
    this.captchaFinishEvent.emit("captchaFailed", errorModel);
  }

  public reset(autoExpired: boolean = false): void {

    if (this.timerId) clearTimeout(this.timerId);

    if (this.apiManager) {
      this.apiManager.reset();
      this.apiManager = null;
    }

    this.honeypotFields.reset();

    this.verificationToken = null;
    if (this.verificationTokenField) {
      this.verificationTokenField.value = '';
      if (this.formElement && this.formElement.contains(this.verificationTokenField)) {
        this.formElement.removeChild(this.verificationTokenField);
      }
    }

    this.eventTracker.reset();

    if (autoExpired) {
      this.captchaFinishEvent.emit("captchaExpired");
    }

    this.switchStatus(Status.START);
    this.captchaFinishEvent.emit("captchaReset");
  }

  public setNewConfig(config: Config): void {
    this.config = config;
  }

  public destroy(): void {
    if (this.timerId) clearTimeout(this.timerId);
    if (this.apiManager) {
      this.apiManager.reset();
      this.apiManager = null;
    }
    this.unregisterElementFocusListeners();
    this.honeypotFields.cleanup();
    this.eventTracker.destroy();
  }
}
